<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12" lg="10" offset-lg="1">
        <div class="d-flex justify-space-between">
          <h2 class="title mb-3">タググループ編集</h2>
          <v-btn class="ma-5" color="success" @click="displayDialog">
            タグを追加する
            <v-icon right> mdi-plus</v-icon>
          </v-btn>
        </div>

        <permission-guard situation="tag_management">

          <v-alert dense outlined type="error" class="text-caption">
            ※1. 優先度は0が最高で、数が大きくなるほど優先度は低くなります<br />
            ※2. 優先度が高いほど、先頭で表示されます。
          </v-alert>
          <v-simple-table fixed-header>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-center"></th>
                  <th class="text-center">表示順</th>
                  <th class="text-center">名前</th>
                  <th />
                </tr>
              </thead>

              <draggable
                v-model="mapTagGroups"
                class="p-postListRow__block"
                ghost-class="p-postListRow__block--ghost"
                tag="tbody"
                handle=".p-postListRow__block--dragging-handle"
                :scroll-sensitivity="200"
                :force-fallback="true"
                :animation="200"
              >
                <tr
                  v-for="(mapTagGroup, index) in mapTagGroups"
                  :key="mapTagGroup.id"
                  class="p-postListRow__block"
                >
                  <td
                    class="col-1 text-center p-postListRow__block--dragging-handle"
                  >
                    <v-icon class="">mdi-view-headline</v-icon>
                  </td>
                  <td class="text-center">
                    {{ index }}
                  </td>
                  <td class="text-center">
                    {{ mapTagGroup.tag.name }}
                  </td>
                  <td class="text-center">
                    <v-btn
                      tile
                      outlined
                      small
                      color="error"
                      @click="removeTag(mapTagGroup.tag)"
                    >
                      グループから外す
                    </v-btn>
                  </td>
                </tr>
                <v-btn class="ma-5" color="primary" @click="update()">
                  更新
                </v-btn>
              </draggable>
            </template>
          </v-simple-table>
        </permission-guard>
      </v-col>
    </v-row>

    <v-dialog v-model="dialog" max-width="450">
      <v-card>
        <v-card-title> タググループに追加</v-card-title>
        <v-card-text>
          <v-form ref="tagForm" v-model="valid" lazy-validation>
            <tag-selector
              v-if="dialog"
              label="タグ名"
              :rules="[presenceValidation]"
              :filter="SelectableTagsFilter"
              @change-tag="setTag"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="green darken-1" text @click="addTag">追加する</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <DataLoader :loading="loading" />
  </v-container>
</template>

<script>
import axios from 'axios'
import draggable from 'vuedraggable'
import PermissionGuard from '@/components/commons/PermissionGuard'
import headUtilsMixin from '@/mixins/head_utils'
import { deserialize } from 'deserialize-json-api'
import DataLoader from '@/components/commons/DataLoader'
import TagSelector from '@/components/commons/tags/TagSelector'
import formValidationUtils from '@/mixins/form_validation_utils'

export default {
  components: {
    TagSelector,
    PermissionGuard,
    DataLoader,
    draggable
  },
  mixins: [headUtilsMixin, formValidationUtils],
  data: () => {
    return {
      pageTitle: 'タググループ編集',
      page: 1,
      loading: true,
      valid: true,
      dialog: false,
      mapTagGroups: [],
      tag: null,
      priority: 0,
      removeTags: []
    }
  },
  mounted: function () {
    axios
      .get(`/api/tag_groups/${this.$route.params.tag_group_id}/edit`)
      .then(
        ({ data }) => {
          this.mapTagGroups = deserialize(data).data
        },
        (error) => {
          const payload = {
            status: error.response.status,
            message: error.response.data.messages
          }
          this.$store.dispatch('SystemMessageStore/createMessage', payload)
        }
      )
      .finally(() => {
        this.loading = false
      })
  },
  methods: {
    validateForm () {
      this.valid = this.$refs.tagForm.validate()
    },
    displayDialog () {
      this.priority = 0
      this.dialog = true
    },
    addTag () {
      this.validateForm()
      if (!this.valid) {
        return false
      }
      this.$store.dispatch('LoaderStore/hideLoader')
      this.mapTagGroups.push({
        priority: this.priority,
        tag: {
          ...this.tag,
          id: this.tag.id,
          name: this.tag.name
        }
      })
      this.removeTags = this.removeTags.filter(tagId => tagId !== this.tag.id)
      this.priority = 0
      const payload = {
        status: 200,
        message: 'タググループにタグを追加しました。'
      }
      this.$store.dispatch('SystemMessageStore/createMessage', payload)
      this.dialog = false
    },
    update () {
      this.$store.dispatch('LoaderStore/displayLoader')
      const removeTagIds = Array.from(
        new Set([
          ...this.removeTags,
          ...this.mapTagGroups.map((group) => group.tag.id)
        ])
      )
      const query = {
        remove_tag_ids: removeTagIds,
        map_tag_groups_attributes: this.mapTagGroups
          .filter((group) => !this.removeTags.includes(group.tag.id))
          .map((group, index) => {
            return {
              tag_group_id: this.$route.params.tag_group_id,
              tag_id: group.tag.id,
              priority: index
            }
          })
      }
      axios
        .patch(`/api/tag_groups/${this.$route.params.tag_group_id}`, query)
        .then(
          ({ data }) => {
            this.$store.dispatch('LoaderStore/hideLoader')

            this.mapTagGroups = deserialize(data).data
            this.removeTags = []
            this.editDialog = false
            const payload = {
              status: 200,
              message: '変更を保存しました。'
            }
            this.$store.dispatch('SystemMessageStore/createMessage', payload)
          },
          (error) => {
            this.$store.dispatch('LoaderStore/hideLoader')
            const payload = {
              status: error.response.status,
              message: error.response.data.message
            }
            this.$store.dispatch('SystemMessageStore/createMessage', payload)
          }
        )
    },
    setTag (value) {
      this.tag = value
    },
    removeTag (tag) {
      this.removeTags.push(tag.id)
      const index = this.mapTagGroups.findIndex(
        (value) => value.tag.id === tag.id
      )
      this.mapTagGroups.splice(index, 1)
    },
    SelectableTagsFilter (item, queryText, itemText) {
      return (
        !this.mapTagGroups.find(
          (mapTagGroup) => item.id === mapTagGroup.tag.id
        ) &&
        (item.id.toString() === queryText || itemText.includes(queryText))
      )
    }
  }
}
</script>
<style scoped>
th,
td {
  vertical-align: middle;
}
</style>
